<template>
  <div class="field-date-edit position-relative">
    <!-- Botón de lock/unlock para campos safe (solo si no está deshabilitado) -->
    <div v-if="field.safe && !disableLock" class="float-left">
      <button
        v-if="isLocked"
        type="button"
        class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
        @click="unlockField"
      >
        <i class="material-icons">lock</i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
        @click="lockField"
      >
        <i class="material-icons">lock_open</i>
      </button>
    </div>

    <!-- Campo de fecha simple (sin hora) -->
    <InputDateDmy
      v-if="!field.time && !field.isArray"
      :value="formatDateForInput(displayValue)"
      :input-id="computedFieldId"
      :input-class="{
        'calculated-field': isLocked,
        'readonly-field': calculated || field.readonly,
      }"
      :hide-picker="isLocked || calculated || field.readonly"
      @input="onDateChange"
      @blur="onBlur"
      @focus="onFocus"
    />

    <!-- Campo de fecha y hora -->
    <input
      v-else-if="field.time && !field.isArray"
      type="datetime-local"
      :value="formatDateTimeForInput(displayValue)"
      :id="computedFieldId"
      class="form-control"
      :class="{
        'calculated-field': isLocked,
        'readonly-field': calculated || field.readonly,
      }"
      style="background: none"
      :placeholder="placeholder"
      @input="onDateTimeChange($event.target.value)"
      @change="onDateTimeChange($event.target.value)"
      @blur="onBlur"
      @focus="onFocus"
    />
    <small class="text-danger" v-if="field.time && !field.isArray">
      {{ $t('Empty time is not allowed') }}
    </small>

    <!-- Array de fechas (no común, pero soportado) -->
    <div v-else-if="field.isArray" class="date-array">
      <div
        v-for="(dateItem, index) in arrayValue"
        :key="index"
        class="date-array-item mb-2"
      >
        <input
          v-if="field.time"
          type="datetime-local"
          :value="formatDateTimeForInput(dateItem)"
          :id="`${computedFieldId}_${index}`"
          class="form-control"
          :class="{
            'calculated-field': isLocked,
            'readonly-field': calculated || field.readonly,
          }"
          style="background: none"
          @change="onArrayDateChange(index, $event.target.value)"
          @blur="onBlur"
          @focus="onFocus"
        />
        <InputDateDmy
          v-else
          :value="formatDateForInput(dateItem)"
          :input-id="`${computedFieldId}_${index}`"
          :input-class="{
            'calculated-field': isLocked,
            'readonly-field': calculated || field.readonly,
          }"
          :hide-picker="isLocked || calculated || field.readonly"
          @input="onArrayDateChange(index, $event)"
          @blur="onBlur"
          @focus="onFocus"
        />
        <button
          v-if="!field.readonly && !isLocked"
          type="button"
          class="btn btn-sm btn-danger ms-2"
          @click="removeArrayItem(index)"
        >
          <i class="material-icons">delete</i>
        </button>
      </div>
      <button
        v-if="!field.readonly && !isLocked"
        type="button"
        class="btn btn-sm btn-secondary"
        @click="addArrayItem"
      >
        <i class="material-icons">add</i> {{ $t('add') }}
      </button>
    </div>
  </div>
</template>

<script>
import InputDateDmy from "../../common/inputs/InputDateDmy.vue";

export default {
  name: "EditFieldDate",
  components: { InputDateDmy },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Date, Array],
      default: null,
    },
    fieldId: {
      type: String,
      default: null,
    },
    editingFields: {
      type: String,
      default: "",
    },
    disableLock: {
      type: Boolean,
      default: false,
    },
    calculated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activelyEditing: false,
      arrayValue: [],
    };
  },
  watch: {
    value: {
      handler(newValue) {
        if (this.field.isArray) {
          this.arrayValue = Array.isArray(newValue) ? [...newValue] : [];
        }
      },
      immediate: true,
    },
  },
  computed: {
    displayValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        return this.value;
      }
      if (this.field.defaultValue !== undefined && this.field.defaultValue !== null) {
        return this.field.defaultValue;
      }
      return null;
    },
    placeholder() {
      return this.field.label || this.field.key || "";
    },
    computedFieldId() {
      return this.fieldId || `field-date-${this.field.key}`;
    },
    isLocked() {
      if (this.activelyEditing) {
        return false;
      }
      if (!this.value) {
        return false;
      }
      if (this.field.safe && this.editingFields !== this.field.key) {
        return true;
      }
      return false;
    },
  },
  methods: {
    formatDateForInput(value) {
      if (!value) return "";
      if (typeof value === "string") {
        if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return value;
        }
        if (value.includes("T")) {
          return value.split("T")[0];
        }
      }
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
      return "";
    },
    formatDateTimeForInput(value) {
      if (!value) return "";
      if (typeof value === "string") {
        if (
          value.includes("T") &&
          !value.includes("Z") &&
          !value.includes("+") &&
          !value.match(/T\d{2}:\d{2}-\d{2}:\d{2}$/)
        ) {
          if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
            return value;
          }
          if (value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            return value.substring(0, 16);
          }
        }
        if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return value + "T00:00";
        }
        if (value.includes("Z") || value.includes("+") || value.match(/-\d{2}:\d{2}$/)) {
          const match = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
          if (match) {
            return match[1];
          }
        }
      }
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        const hours = String(value.getHours()).padStart(2, "0");
        const minutes = String(value.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      return "";
    },
    onDateChange(value) {
      if (typeof value === "string") {
        if (value.includes("T")) {
          let valuedate = value.split("T")[0];
          this.onFieldChange(valuedate);
        } else {
          this.onFieldChange(value);
        }
      }
    },
    onDateTimeChange(value) {
      this.onFieldChange(value);
    },
    onArrayDateChange(index, value) {
      const newArray = [...this.arrayValue];
      if (this.field.time && value.includes("T")) {
        newArray[index] = value;
      } else if (!this.field.time) {
        newArray[index] = value.includes("T") ? value.split("T")[0] : value;
      } else {
        newArray[index] = value;
      }
      this.arrayValue = newArray;
      this.onFieldChange(newArray);
    },
    addArrayItem() {
      const newArray = [...this.arrayValue];
      const defaultValue = this.field.defaultValue || (this.field.time ? "" : "");
      newArray.push(defaultValue);
      this.arrayValue = newArray;
      this.onFieldChange(newArray);
    },
    removeArrayItem(index) {
      const newArray = [...this.arrayValue];
      newArray.splice(index, 1);
      this.arrayValue = newArray;
      this.onFieldChange(newArray);
    },
    onFieldChange(newValue) {
      this.activelyEditing = true;
      this.$emit("input", newValue);
    },
    onBlur() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = false;
      }
    },
    onFocus() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = true;
      }
    },
    lockField() {
      this.$emit("update:editingFields", "");
      this.activelyEditing = false;
    },
    unlockField() {
      this.$emit("update:editingFields", this.field.key);
      this.activelyEditing = true;
    },
  },
};
</script>

<style scoped>
.field-date-edit {
  width: 100%;
}

.form-control {
  padding: 0 7px !important;
}

::v-deep .readonly-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  color: #6c757d !important;
  cursor: not-allowed !important;
  opacity: 0.8;
}

::v-deep .calculated-field {
  background-color: #f8f9fa !important;
  border-color: #dee2e6 !important;
  cursor: not-allowed !important;
}

::v-deep .readonly-field:focus {
  box-shadow: none !important;
  border-color: #dee2e6 !important;
}

.date-array-item {
  display: flex;
  align-items: center;
}

</style>

